import { fromJS, Map } from 'immutable'
import config from 'config'
import { parseTransformer } from '@sellpy/commons'
import { runCloudCached, parseObject } from '../lib/parseTools'
import Parse from '../../client/lib/parse'
import { getAlgoliaNonItemApplication } from '../lib/algolia'
import { cacheEntities } from '../entities/actions'
import { showToast, TOAST_TYPE } from '../../client/uiComponents/notifications/toastState'
import { getCurrentParseUser } from '../user/currentUser'
import { cachePricingModel } from '../cart/actions.js'
import { getIsBusinessSaleSelector, itemValuesSelector } from './selectors'

export const ITEM_LOADING = 'ITEM_LOADING'
export const ITEM_SUCCESS = 'ITEM_SUCCESS'
export const ITEM_ERROR = 'ITEM_ERROR'

export function getItem(itemId) {
  return (dispatch) => {
    dispatch({ type: ITEM_LOADING })
    return Parse.Cloud.run('getPdpItem', { itemId })
      .then((item) => {
        dispatch(cacheEntities(item))
        dispatch({ type: ITEM_SUCCESS })
        return parseTransformer.normalize(item).getIn(['Item', itemId])
      })
      .catch((error) => {
        dispatch({ type: ITEM_ERROR, payload: error })
        throw error
      })
  }
}

export const GET_MARKET_OFFER_LOADING = 'GET_MARKET_OFFER_LOADING'
export const GET_MARKET_OFFER_SUCCESS = 'GET_MARKET_OFFER_SUCCESS'
export const GET_MARKET_OFFER_ERROR = 'GET_MARKET_OFFER_ERROR'

export const GET_MARKET_FIRST_INVENTORY_LOADING = 'GET_MARKET_FIRST_INVENTORY_LOADING'
export const GET_MARKET_FIRST_INVENTORY_SUCCESS = 'GET_MARKET_FIRST_INVENTORY_SUCCESS'
export const GET_MARKET_FIRST_INVENTORY_ERROR = 'GET_MARKET_FIRST_INVENTORY_ERROR'

export const getLatestMarketInventoryForItem = (itemId) => async (dispatch) => {
  dispatch({ type: GET_MARKET_FIRST_INVENTORY_LOADING })
  return runCloudCached((marketInventory) => {
    dispatch(cacheEntities(marketInventory))
  })('getLatestMarketInventoryForItem', { itemId })
}

const SALE_HISTORY_LOADING = 'SALE_HISTORY_LOADING'
export const SALE_HISTORY_SUCCESS = 'SALE_HISTORY_SUCCESS'

const getSaleHistoryForOwnItem = (itemId) => {
  return async (dispatch) => {
    dispatch({ type: SALE_HISTORY_LOADING })
    const saleHistory = await Parse.Cloud.run('getSaleHistoryForOwnItem', { itemId })
    if (saleHistory) {
      dispatch({
        type: SALE_HISTORY_SUCCESS,
        saleHistory: fromJS({ [itemId]: saleHistory.saleHistory })
      })
      dispatch(cacheEntities(saleHistory.claim))
      dispatch(cacheEntities(saleHistory.orders))
    }
  }
}

export const SALE_FUTURE_SUCCESS = 'SALE_FUTURE_SUCCESS'

const whereWasItemSoldPromises = {}

export const WHERE_WAS_ITEM_SOLD_SUCCESS = 'WHERE_WAS_ITEM_SOLD_SUCCESS'
export const WHERE_WAS_ITEM_SOLD_ERROR = 'WHERE_WAS_ITEM_SOLD_ERROR'

const fetchWhereWasItemSold = (itemId) => async (dispatch) => {
  return runCloudCached((offer) => {
    dispatch(cacheEntities(offer))
    dispatch({
      type: WHERE_WAS_ITEM_SOLD_SUCCESS,
      itemId,
      offerName: offer.className,
      offerId: offer.id
    })
  })('whereWasItemSold', { itemId }).catch((error) => {
    console.error(error)
    dispatch({ type: WHERE_WAS_ITEM_SOLD_ERROR, itemId })
    return Promise.reject(error)
  })
}

export const whereWasItemSold = (itemId) => {
  return (dispatch, getState) => {
    const cachedOffer = getState().items.whereWasItemSold.get(itemId)
    if (cachedOffer || cachedOffer === null) return Promise.resolve(cachedOffer)
    if (whereWasItemSoldPromises[itemId]) return whereWasItemSoldPromises[itemId]
    whereWasItemSoldPromises[itemId] = dispatch(fetchWhereWasItemSold(itemId))
    return whereWasItemSoldPromises[itemId]
  }
}

export const GET_ITEM_PRICE_ESTIMATE = 'GET_ITEM_PRICE_ESTIMATE'

export const getItemPriceEstimate = (itemId) => (dispatch) => {
  if (!itemId) return
  return runCloudCached((priceEstimate) =>
    dispatch({
      type: GET_ITEM_PRICE_ESTIMATE,
      priceEstimate: fromJS(priceEstimate)
    })
  )('itemPriceEstimate', { itemId, region: process.env.REGION })
}

const NR_SIMILAR_BRANDS = 20
// Endpoint needs to supply Access-Control-Allow-Origin
export const fetchSimilarBrands = (brand) => {
  return fetch(config.algolia.similarBrands, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      request: {
        brand: {
          n: NR_SIMILAR_BRANDS,
          value: brand
        }
      }
    })
  })
    .then((response) => response.json())
    .catch((error) => {
      if (error.message === 'Failed to fetch') return { result: {} }
      else return Promise.reject(error)
    })
}

export const createSellerReturnReservation = (itemId, t) => {
  return (dispatch) => {
    return Parse.Cloud.run('createSellerReturnReservation', { itemId })
      .then(({ item, sale, reservation, pricingModel }) => {
        dispatch(cachePricingModel({ sale, pricingModel }))
        dispatch(cacheEntities(item))
        dispatch(cacheEntities(sale))
        dispatch(cacheEntities(reservation))
        dispatch(getSaleHistoryForOwnItem(item.id))
        showToast({
          header: t('sellerReturn.confirmToast.header'),
          body: t('sellerReturn.confirmToast.body'),
          type: TOAST_TYPE.SUCCESS
        })
      })
      .catch((error) => {
        showToast({
          header: t('common:errorToast.header'),
          body: t('common:errorToast.body'),
          type: TOAST_TYPE.ERROR
        })
        console.error(error)
        return Promise.reject(error)
      })
  }
}

export const removeSellerReturnReservation = (reservationId) => {
  return (dispatch) => {
    return Parse.Cloud.run('removeSellerReturnReservation', { reservationId })
      .then(({ item, reservation }) => {
        dispatch(cacheEntities(item))
        dispatch(cacheEntities(reservation))
        dispatch(getSaleHistoryForOwnItem(item.id))
      })
      .catch(console.error)
  }
}

export const fetchItemIO = (itemIOId) => (dispatch, getState) => {
  if (!itemIOId) return
  if (getState().entities.ItemIO.get(itemIOId)) return Promise.resolve()
  return runCloudCached((itemIO) => {
    dispatch(cacheEntities(itemIO))
    return itemIO
  })('fetchItemIO', { itemIOId }).catch(console.error)
}

export const FETCH_BRANDS = 'FETCH_BRANDS'

const { searchSingleIndex, brandIndex, getItemTypeIndex } = getAlgoliaNonItemApplication()

export const getBrands = (query) => {
  return async (dispatch) => {
    const response = await searchSingleIndex({
      indexName: brandIndex,
      searchParams: { query, hitsPerPage: 1000 }
    })
    dispatch({ type: FETCH_BRANDS, brands: fromJS(response.hits) })
  }
}

export const FETCH_TYPES = 'FETCH_TYPES'

export const getTypes = (locale) => (query) => {
  return async (dispatch) => {
    const response = await searchSingleIndex({
      indexName: getItemTypeIndex(locale),
      searchParams: { query, hitsPerPage: 1000 }
    })
    dispatch({ type: FETCH_TYPES, itemTypes: fromJS(response.hits) })
  }
}

export const getLatestItemValuation = (itemId) => async (dispatch) => {
  const itemValuation = await Parse.Cloud.run('getLatestItemValuation', { itemId })
  dispatch(cacheEntities(itemValuation))
  return itemValuation
}

export const getOwnOrderForItem = (itemId) => async (dispatch) => {
  // TODO: this function has a parsoku equivalent now
  const order = await new Parse.Query('Order')
    .equalTo('items', parseObject('Item', itemId))
    .equalTo('user', getCurrentParseUser())
    .descending('createdAt')
    .notEqualTo('status', 'canceled')
    .include('items')
    .first()
  dispatch(cacheEntities(order))
}

export const getP2pOrderForItem = (itemId) => async (dispatch) => {
  const order = await Parse.Cloud.run('getP2pOrderForItem', { itemId })
  dispatch(cacheEntities(order))
  return order
}

export const fetchSucceedingItemCopy = (itemId) => (dispatch) =>
  runCloudCached((succeedingItem) => dispatch(cacheEntities(succeedingItem)))(
    'fetchSucceedingItemCopy',
    {
      itemId
    }
  )

export const p2pRequestItem =
  ({ itemId, priceSuggestion }) =>
  async (dispatch) => {
    const buyRequest = await Parse.Cloud.run('p2pRequestItem', {
      itemId,
      priceSuggestion,
      region: process.env.REGION
    })
    dispatch(cacheEntities(buyRequest))
  }

export const getP2pRequestsForItem = (itemId) => async (dispatch) => {
  const buyRequests = await Parse.Cloud.run('getP2pRequestsForItem', { itemId })
  dispatch(cacheEntities(buyRequests))
}

export const declineP2pBuyRequests = (itemId) => async (dispatch) => {
  const declinedBuyRequests = await Parse.Cloud.run('declineP2pBuyRequestsForItem', { itemId })
  dispatch(cacheEntities(declinedBuyRequests))
}

export const IS_BUSINESS_SALES = 'IS_BUSINESS_SALES'

export const getIsBusinessSales = (items) => async (dispatch, getState) => {
  const isBusinessSaleByItem = getIsBusinessSaleSelector(getState(), items)
  const itemsToFetch = items.filter(
    (item) => isBusinessSaleByItem.get(item.get('objectId')) === undefined
  )
  if (itemsToFetch.length === 0) return
  dispatch({
    type: IS_BUSINESS_SALES,
    isBusinessSalesMap: itemsToFetch.reduce((map, item) => map.set(item.get('bag'), null), Map())
  })
  Parse.Cloud.run('getIsBusinessSales', {
    itemIds: itemsToFetch.map((item) => item.get('objectId'))
  }).then((isBusinessSalesMap) =>
    dispatch({ type: IS_BUSINESS_SALES, isBusinessSalesMap: fromJS(isBusinessSalesMap) })
  )
}

export const FETCH_EXPECTED_SHIPPING_DATES = 'FETCH_EXPECTED_SHIPPING_DATES'

export const ITEMS_PREVIOUSLY_CART = 'ITEMS_PREVIOUSLY_CART'

export const ITEM_VALUES = 'ITEM_VALUES'

export const getItemValues = (itemIds) => async (dispatch, getState) => {
  itemIds = itemIds.filter(Boolean)
  if (itemValuesSelector(itemIds)(getState()).every((value) => value !== undefined)) return
  // Set values to null to indicate fetching has started and prevent duplicate fetches for the same items
  dispatch({
    type: ITEM_VALUES,
    itemValues: itemIds.reduce((map, itemId) => map.set(itemId, null), Map())
  })
  const itemValues = await Parse.Cloud.run('getItemValues', { itemIds })
  dispatch({
    type: ITEM_VALUES,
    itemValues: fromJS(itemValues)
  })
}

export const getItemValue = (itemId, cacheOptions) => async (dispatch) => {
  return runCloudCached(
    (itemValues) =>
      dispatch({
        type: ITEM_VALUES,
        itemValues: fromJS(itemValues)
      }),
    cacheOptions
  )('getItemValues', {
    itemIds: [itemId]
  })
}

export const getDonateAfter = async (itemId) => {
  return runCloudCached()('getDonateAfter', { itemId }).catch(console.error)
}
